/**
 * The branch the screens are scoped to. All-branch users can pick "All branches" or
 * one branch; everyone else picks among their assigned branches. The API enforces
 * isolation (BranchScope) — this only chooses the filter that list queries send
 * as ?branchId=. The choice is remembered per browser.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './auth';
import { useBranches } from './queries';
import type { BranchRef } from './types';

export const ALL = 'all';

interface BranchState {
  /** 'all' or a branch id. */
  branchId: string;
  setBranchId: (id: string) => void;
  /** The selected branch, or null for all. */
  branch: BranchRef | null;
  options: BranchRef[];
  canSeeAll: boolean;
  /** Value for ?branchId= (undefined = all the user may see). */
  queryValue: string | undefined;
}

const Ctx = createContext<BranchState | null>(null);
const KEY = 'eros.branch';

export function BranchProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const branches = useBranches();
  // The API already scopes /branches to what the user may see; filter again by assignment to be safe.
  const options = useMemo(() => {
    const list = branches.data ?? [];
    return user?.allBranches ? list : list.filter((b) => user?.branchIds.includes(b.id));
  }, [branches.data, user]);
  const canSeeAll = !!user && (user.allBranches || options.length > 1);
  const [branchId, setRaw] = useState<string>(() => {
    try {
      return localStorage.getItem(KEY) ?? ALL;
    } catch {
      return ALL;
    }
  });

  // Keep the choice valid for this user: a single-branch user is pinned to it; a
  // multi-branch user without a saved choice starts on their default branch.
  useEffect(() => {
    if (!user || !branches.data) return;
    const valid = branchId === ALL ? canSeeAll : options.some((b) => b.id === branchId);
    if (!valid) setRaw(canSeeAll && user.allBranches ? ALL : user.defaultBranchId && options.some((b) => b.id === user.defaultBranchId) ? user.defaultBranchId : options[0]?.id ?? ALL);
  }, [user, branches.data, options, canSeeAll, branchId]);

  const value = useMemo<BranchState>(() => {
    const branch = options.find((b) => b.id === branchId) ?? null;
    return {
      branchId,
      branch,
      options,
      canSeeAll,
      queryValue: branch ? branch.id : undefined,
      setBranchId: (id) => {
        setRaw(id);
        try {
          localStorage.setItem(KEY, id);
        } catch {
          /* private mode: lasts this visit */
        }
      },
    };
  }, [branchId, options, canSeeAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBranch(): BranchState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBranch outside BranchProvider');
  return v;
}
